import { useEffect, useState } from 'react';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import {
  LayoutDashboard, Ticket, FileText, Users, Mail, Settings, LogOut, CalendarDays,
  Menu, X, Workflow, Send, QrCode, Frame, UserCog, Loader2, Receipt,
} from 'lucide-react';
import axios from 'axios';
import AdminCursor from './components/AdminCursor';
import { canRoleAccess, useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full nav — filtered per role at render time.
// `roles` = which roles may see this link. Absent = all.
const NAV_ITEMS = [
  { path: '/admin/dashboard',       label: 'Dashboard',       icon: LayoutDashboard, roles: ['admin'] },
  { path: '/admin/exhibitions',     label: 'Exhibitions',     icon: CalendarDays,     roles: ['admin'] },
  { path: '/admin/exhibition-flow', label: 'Exhibition Flow', icon: Workflow,         roles: ['admin'] },
  { path: '/admin/tickets',         label: 'Tickets',         icon: Ticket,           roles: ['admin'] },
  { path: '/admin/open-call',       label: 'Open Call',       icon: FileText,         roles: ['admin', 'curator'] },
  { path: '/admin/artists',         label: 'Artists',         icon: Users,            roles: ['admin', 'curator'] },
  { path: '/admin/artworks',        label: 'Artworks',        icon: Frame,            roles: ['admin'] },
  { path: '/admin/sales',           label: 'Sales',           icon: Receipt,          roles: ['admin'] },
  { path: '/admin/invite',          label: 'Invite',          icon: Send,             roles: ['admin'] },
  { path: '/admin/email-console',   label: 'Email Console',   icon: Mail,             roles: ['admin', 'curator'] },
  { path: '/admin/qr',              label: 'QR Codes',        icon: QrCode,           roles: ['admin', 'curator'] },
  { path: '/admin/users',           label: 'Users',           icon: UserCog,          roles: ['admin'] },
  { path: '/admin/settings',        label: 'Settings',        icon: Settings,         roles: ['admin'] },
];

function filterNavForRole(role) {
  return NAV_ITEMS.filter((it) => !it.roles || it.roles.includes(role));
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checking, logout } = useAuth();
  const [exhibitions, setExhibitions] = useState([]);
  const [selectedExhibition, setSelectedExhibition] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Gate: force login when there's no session (and we're not still verifying).
  useEffect(() => {
    if (!checking && !user) navigate('/admin/login', { replace: true });
  }, [checking, user, navigate]);

  // Belt-and-suspenders: curator trying to reach an admin-only URL → redirect.
  useEffect(() => {
    if (!user) return;
    if (!canRoleAccess(user.role, location.pathname)) {
      const fallback = user.role === 'curator' ? '/admin/open-call' : '/admin/dashboard';
      navigate(fallback, { replace: true });
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/exhibitions`).then((res) => {
      setExhibitions(res.data);
      const stored = localStorage.getItem('admin_exhibition_id');
      const found = res.data.find((e) => e.id === stored);
      setSelectedExhibition(found || res.data[0] || null);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (selectedExhibition) {
      localStorage.setItem('admin_exhibition_id', selectedExhibition.id);
    }
  }, [selectedExhibition]);

  if (checking || !user) {
    return (
      <div className="admin-root min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
      </div>
    );
  }

  const navItems = filterNavForRole(user.role);
  const exhibitionId = selectedExhibition?.id || '';

  return (
    <div className="admin-root flex h-screen bg-[#0a0a0a] text-[#f6f6f6] overflow-hidden">
      <AdminCursor />
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex w-60 flex-col border-r border-[#1a1a1a] bg-[#070707]">
        <div className="px-5 pt-6 pb-4 border-b border-[#1a1a1a]">
          <p className="text-xs font-mono tracking-[0.2em] text-[#90E0EF] uppercase">The Relative Gallery</p>
          <p className="text-[10px] font-mono text-[#666] tracking-[0.15em] mt-1">
            ROLE · <span className="text-[#c8c8c8]">{user.role.toUpperCase()}</span>
          </p>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace('/admin/', '')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded text-xs font-mono tracking-wide transition-all duration-200 ${
                  isActive ? 'bg-[#90E0EF]/10 text-[#90E0EF] border border-[#90E0EF]/20' : 'text-[#888] hover:text-[#f6f6f6] hover:bg-[#111] border border-transparent'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1a1a1a]">
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-xs font-mono text-[#666] hover:text-red-400 transition-colors w-full"
            style={{ cursor: 'pointer' }}
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile menu button */}
      <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden fixed top-4 left-4 z-50 text-[#f6f6f6] bg-[#111] border border-[#222] p-2 rounded" style={{ cursor: 'pointer' }}>
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            initial={{ x: -240 }}
            animate={{ x: 0 }}
            exit={{ x: -240 }}
            className="md:hidden fixed inset-y-0 left-0 w-60 z-40 flex flex-col border-r border-[#1a1a1a] bg-[#070707]"
          >
            <div className="px-5 pt-6 pb-4 border-b border-[#1a1a1a]">
              <p className="text-xs font-mono tracking-[0.2em] text-[#90E0EF] uppercase">The Relative Gallery</p>
              <p className="text-[10px] font-mono text-[#666] tracking-[0.15em] mt-1">
                ROLE · <span className="text-[#c8c8c8]">{user.role.toUpperCase()}</span>
              </p>
            </div>
            <nav className="flex-1 py-4 px-3 space-y-1">
              {navItems.map((item) => (
                <NavLink key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded text-xs font-mono tracking-wide transition-all ${isActive ? 'bg-[#90E0EF]/10 text-[#90E0EF]' : 'text-[#888] hover:text-white'}`}>
                  <item.icon className="w-4 h-4" />{item.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-[#1a1a1a]">
              <button onClick={logout} className="flex items-center gap-2 text-xs font-mono text-[#666] hover:text-red-400" style={{ cursor: 'pointer' }}>
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 flex items-center justify-between px-6 border-b border-[#1a1a1a] bg-[#070707] shrink-0">
          <div className="flex items-center gap-4">
            {exhibitions.length > 0 && (
              <>
                <span className="text-xs font-mono text-[#666] hidden sm:block">Exhibition:</span>
                <select
                  value={exhibitionId}
                  onChange={(e) => {
                    const ex = exhibitions.find((x) => x.id === e.target.value);
                    if (ex) setSelectedExhibition(ex);
                  }}
                  className="bg-[#111] border border-[#222] text-[#f6f6f6] text-xs font-mono px-3 py-1.5 rounded-none focus:outline-none focus:border-[#90E0EF]"
                  style={{ cursor: 'pointer' }}
                >
                  {exhibitions.map((ex) => (
                    <option key={ex.id} value={ex.id}>{ex.title}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <span className="text-[10px] font-mono text-[#666] tracking-wider hidden sm:block" data-testid="admin-user-email">
            {user.full_name ? `${user.full_name} · ${user.email}` : user.email}
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet context={{ exhibitionId, selectedExhibition, exhibitions, setExhibitions, setSelectedExhibition, currentUser: user }} />
        </main>
      </div>

      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: '#111', border: '1px solid #222', color: '#f6f6f6', borderRadius: 0, fontFamily: '"JetBrains Mono", monospace', fontSize: '12px' } }} />
    </div>
  );
}
